import { useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useMyShift, useRsMutation, useShifts, type Shift } from '../api/restaurant';
import { formatDateTime, formatGhs, parseCedis } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

const TENDER_LABEL: Record<string, string> = { CASH: 'Cash', MOMO: 'Mobile money', CARD: 'Card' };

/** Your till session: the float you start with, what you took, and the count at the end. */
export function Till() {
  const { can } = useAuth();
  const mine = useMyShift(true);
  const all = useShifts(can('shift.close_any'));
  const toast = useToast();
  const report = useReport();
  const [float, setFloat] = useState('');
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const open = useRsMutation((openingFloat: number) => api('/shifts/open', { method: 'POST', body: { openingFloat } }));
  const close = useRsMutation((v: { id: string; countedCash: number; note?: string }) => api<{ shift: Shift }>(`/shifts/${v.id}/close`, { method: 'POST', body: { countedCash: v.countedCash, ...(v.note ? { note: v.note } : {}) } }));

  const s = mine.data;
  const floatP = parseCedis(float === '' ? '0' : float);
  const countedP = parseCedis(counted);
  const expected = s ? s.openingFloat + s.cashTaken - s.cashRefunded : 0;

  return (
    <div className="page">
      <PageHead eyebrow="Restaurant and bar" title="Till" />

      {mine.isLoading ? (
        <Loading rows={2} />
      ) : mine.error ? (
        <ErrorNote error={mine.error} retry={() => void mine.refetch()} />
      ) : !s ? (
        <section className="card">
          <div className="card-head"><h2 className="h2">Open your till</h2></div>
          <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (floatP !== null) open.mutate(floatP, { onError: report, onSuccess: () => { toast.ok('Till open'); setFloat(''); } }); }}>
            <p className="small muted">Count the cash you are starting with. You need an open till to take cash, MoMo or card.</p>
            <Field id="fl" label="Starting cash (GH₵)" error={floatP === null ? 'Enter an amount, or 0' : undefined}>
              <input id="fl" className="input" inputMode="decimal" value={float} placeholder="0.00" onChange={(e) => setFloat(e.target.value)} autoFocus />
            </Field>
            <Button variant="primary" type="submit" disabled={floatP === null} loading={open.isPending}>Open till</Button>
          </form>
        </section>
      ) : (
        <section className="card">
          <div className="card-head">
            <h2 className="h2">Open since {formatDateTime(s.openedAt)}</h2>
            <Badge tone="good">Open</Badge>
          </div>
          <dl className="kv">
            <dt>Starting cash</dt><dd className="num">{formatGhs(s.openingFloat)}</dd>
            {Object.entries(s.byTender).map(([t, v]) => (
              <span key={t} style={{ display: 'contents' }}>
                <dt>{TENDER_LABEL[t] ?? t} taken</dt><dd className="num">{formatGhs(v!.amount)} <span className="muted small">({v!.count})</span></dd>
              </span>
            ))}
            {s.cashRefunded > 0 ? (
              <>
                <dt>Cash refunded</dt>
                <dd className="num">−{formatGhs(s.cashRefunded)}</dd>
              </>
            ) : null}
            <dt>Cash that should be in the till</dt><dd className="num"><strong>{formatGhs(expected)}</strong></dd>
          </dl>
          <form
            className="stack"
            style={{ marginTop: 16 }}
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (countedP === null) return;
              close.mutate({ id: s.id, countedCash: countedP, ...(note.trim() ? { note: note.trim() } : {}) }, {
                onError: report,
                onSuccess: (r) => {
                  const v = (r as { shift: Shift }).shift.variance ?? 0;
                  toast.ok(v === 0 ? 'Till closed. The count is exact.' : `Till closed. ${v < 0 ? 'Short' : 'Over'} by ${formatGhs(Math.abs(v))}.`);
                  setCounted('');
                  setNote('');
                },
              });
            }}
          >
            <div className="eyebrow">Close the till</div>
            <Field id="ct" label="Cash counted (GH₵)" hint={countedP !== null ? (countedP === expected ? 'Matches exactly' : `${countedP < expected ? 'Short' : 'Over'} by ${formatGhs(Math.abs(countedP - expected))}`) : 'Count everything in the drawer'}>
              <input id="ct" className="input" inputMode="decimal" value={counted} onChange={(e) => setCounted(e.target.value)} />
            </Field>
            {countedP !== null && countedP !== expected ? (
              <Field id="cn" label="Note (recommended)" hint="Why is it different?"><input id="cn" className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
            ) : null}
            <Button variant="primary" type="submit" disabled={countedP === null} loading={close.isPending}>Close till</Button>
          </form>
        </section>
      )}

      {can('shift.close_any') ? (
        <section className="stack">
          <div className="eyebrow">Recent till sessions</div>
          {all.isLoading ? (
            <Loading rows={2} />
          ) : (all.data ?? []).length === 0 ? (
            <div className="card flat"><Empty icon="inbox" title="No till sessions yet" /></div>
          ) : (
            <div className="list">
              {all.data!.map((x) => (
                <div key={x.id} className="item">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="title">{x.cashier ?? 'Cashier'}</span>
                    <Badge tone={x.status === 'OPEN' ? 'good' : x.variance === 0 ? 'brand' : 'warn'}>{x.status === 'OPEN' ? 'Open' : x.variance === 0 ? 'Exact' : `${(x.variance ?? 0) < 0 ? 'Short' : 'Over'} ${formatGhs(Math.abs(x.variance ?? 0))}`}</Badge>
                  </div>
                  <span className="small muted">{formatDateTime(x.openedAt)}{x.closedAt ? ` → ${formatDateTime(x.closedAt)}` : ''} · cash taken {formatGhs(x.cashTaken)}</span>
                  {x.note ? <span className="hint">{x.note}</span> : null}
                </div>
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
